import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import { Button, Pagination } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Spinner from '../../../../shared/spinner';
import { ButtonAlterar } from '../../../../shared/buttons';
import { Cessao } from '../../../../shared/model/cessao';
import { format } from 'date-fns';
import CessaoService from '../../../../services/cessaoService';

function PaginationCompacta({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (page: number) => void }) {
  const itens = [];

  // Lógica para criar uma janela em torno da página atual
  const delta = 2; // número de páginas antes/depois da atual a mostrar
  let start = Math.max(0, page - delta);
  let end = Math.min(totalPages - 1, page + delta);

  // Ajusta início ou fim para sempre mostrar 'delta * 2 + 1' páginas se possível
  if (page - delta < 0) {
    end = Math.min(totalPages - 1, end + (delta - page));
  }
  if (page + delta > totalPages - 1) {
    start = Math.max(0, start - (page + delta - (totalPages - 1)));
  }

  // Sempre mostrar primeira página
  itens.push(
    <Pagination.Item
      key={0}
      active={page === 0}
      onClick={() => onPageChange(0)}
    >
      {1}
    </Pagination.Item>
  );

  // Mostrar "..." se houver intervalo entre 1 e start
  if (start > 1) {
    itens.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
  }

  // Mostrar páginas centrais
  for (let i = start; i <= end; i++) {
    if (i !== 0 && i !== totalPages - 1) {
      itens.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => onPageChange(i)}
        >
          {i + 1}
        </Pagination.Item>
      );
    }
  }

  // Mostrar "..." se houver intervalo entre end e última página
  if (end < totalPages - 2) {
    itens.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
  }

  // Sempre mostrar última página se for diferente da primeira
  if (totalPages > 1) {
    itens.push(
      <Pagination.Item
        key={totalPages - 1}
        active={page === totalPages - 1}
        onClick={() => onPageChange(totalPages - 1)}
      >
        {totalPages}
      </Pagination.Item>
    );
  }

  return (
    <Pagination>
      <Pagination.Prev
        onClick={() => onPageChange(Math.max(0, page - 1))}
        disabled={page === 0}
      />
      {itens}
      <Pagination.Next
        onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
        disabled={page === totalPages - 1}
      />
    </Pagination>
  );
}

export default function CessaoComponent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cessoes, setCessoes] = useState<Cessao[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const cessaoService: CessaoService = new CessaoService();

  const listarTodasCessoes = async (pageToLoad = 0) => {
    try {
      setLoading(true);
      const response = await cessaoService.listarTodasCessoes(pageToLoad, 10);
      setCessoes(response.data.content); // content: dados da página atual
      setTotalPages(response.data.totalPages); // total de páginas
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  useEffect(() => {
    listarTodasCessoes(page);
  }, [page]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="position-relative m-md-3">
      <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between' }}>
        <h5>Cessão &gt; Cessão de Carteira</h5>
        <Button
          onClick={() => navigate('/backoffice-tabelas/cessao/inclusao', { state: { id: null } })}
          id="App-button-cor-principal-escura"
        >
          + Nova Cessão
        </Button>
      </div>
      {loading ? <Spinner loading={loading} /> : (
        <>
          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Data Base</th>
                  <th>Valor</th>
                  <th>Taxa %</th>
                  <th>Alteração</th>
                </tr>
              </thead>
              <tbody>
                {cessoes.map((item) => (
                  <tr key={item.id}>
                    <td>{item.codigo}</td>
                    <td>{item.nome}</td>
                    <td>{item.dtInclusao ? format(new Date(item.dtInclusao), 'dd/MM/yyyy') : ''}</td>
                    <td>R$ {item.valor!.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                    <td>{item.taxa}</td>
                    <td>
                      <Link to={`/backoffice-tabelas/cessao/alteracao`} state={{ id: item.id }}>
                        <ButtonAlterar />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <PaginationCompacta page={page} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>

        </>
      )}
    </div>
  );
}
